import * as Schema from "effect/Schema";
import {
  CommandId,
  IsoDateTime,
  NonNegativeInt,
  PositiveInt,
  ProjectId,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";
import { VcsDriverKind } from "./vcs.ts";

export const SourceControlProviderKind = Schema.Literals(["github", "unknown"]);
export type SourceControlProviderKind = typeof SourceControlProviderKind.Type;

export const SourceControlProviderInfo = Schema.Struct({
  kind: SourceControlProviderKind,
  name: TrimmedNonEmptyString,
  baseUrl: Schema.String,
});
export type SourceControlProviderInfo = typeof SourceControlProviderInfo.Type;

export const ChangeRequestState = Schema.Literals(["open", "closed", "merged"]);
export type ChangeRequestState = typeof ChangeRequestState.Type;

export const ChangeRequest = Schema.Struct({
  provider: SourceControlProviderKind,
  number: PositiveInt,
  title: TrimmedNonEmptyString,
  url: Schema.String,
  baseRefName: TrimmedNonEmptyString,
  headRefName: TrimmedNonEmptyString,
  state: ChangeRequestState,
  updatedAt: Schema.Option(Schema.DateTimeUtc),
  author: Schema.optional(
    Schema.NullOr(
      Schema.Struct({
        login: TrimmedNonEmptyString,
        name: Schema.NullOr(Schema.String),
        avatarUrl: Schema.NullOr(Schema.String),
      }),
    ),
  ),
  isDraft: Schema.optional(Schema.Boolean),
  createdAt: Schema.optional(Schema.Option(Schema.DateTimeUtc)),
  reviewRequestLogins: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
  labels: Schema.optional(
    Schema.Array(
      Schema.Struct({
        name: TrimmedNonEmptyString,
        color: Schema.NullOr(Schema.String),
      }),
    ),
  ),
  reviewDecision: Schema.optional(
    Schema.Literals(["approved", "changes-requested", "review-required"]),
  ),
  checksState: Schema.optional(Schema.Literals(["passing", "failing", "pending"])),
  isCrossRepository: Schema.optional(Schema.Boolean),
  headRepositoryNameWithOwner: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  headRepositoryOwnerLogin: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
});
export type ChangeRequest = typeof ChangeRequest.Type;

export const PullRequestInvolvement = Schema.Literals(["all", "reviewing", "authored"]);
export type PullRequestInvolvement = typeof PullRequestInvolvement.Type;

export const PullRequestListState = Schema.Literals(["all", "open", "closed", "merged"]);
export type PullRequestListState = typeof PullRequestListState.Type;

export const PullRequestReviewDecision = Schema.Literals([
  "approved",
  "changes-requested",
  "review-required",
]);
export type PullRequestReviewDecision = typeof PullRequestReviewDecision.Type;

export const PullRequestChecksState = Schema.Literals(["passing", "failing", "pending"]);
export type PullRequestChecksState = typeof PullRequestChecksState.Type;

const PullRequestQualifierValue = TrimmedNonEmptyString.check(Schema.isMaxLength(200));
const PullRequestQualifierValues = Schema.Array(PullRequestQualifierValue).check(
  Schema.isMaxLength(10),
);

export const PullRequestListFilters = Schema.Struct({
  draft: Schema.optional(Schema.Literals(["only", "hide"])),
  review: Schema.optional(
    Schema.Literals(["approved", "changes-requested", "review-required", "none"]),
  ),
  checks: Schema.optional(Schema.Literals(["passing", "failing"])),
  author: Schema.optional(PullRequestQualifierValue),
  labels: Schema.optional(Schema.Array(PullRequestQualifierValues).check(Schema.isMaxLength(10))),
  excludedLabels: Schema.optional(PullRequestQualifierValues),
});
export type PullRequestListFilters = typeof PullRequestListFilters.Type;

export const PullRequestListInput = Schema.Struct({
  state: PullRequestListState,
  involvement: Schema.optional(PullRequestInvolvement),
  projectIds: Schema.optional(Schema.Array(ProjectId).check(Schema.isMaxLength(100))),
  query: Schema.optional(PullRequestQualifierValue),
  filters: Schema.optional(PullRequestListFilters),
  limit: Schema.optional(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 500 }))),
});
export type PullRequestListInput = typeof PullRequestListInput.Type;

export const PullRequestActor = Schema.Struct({
  login: TrimmedNonEmptyString,
  name: Schema.NullOr(Schema.String),
  avatarUrl: Schema.NullOr(Schema.String),
});
export type PullRequestActor = typeof PullRequestActor.Type;

export const PullRequestLabel = Schema.Struct({
  name: TrimmedNonEmptyString,
  color: Schema.NullOr(Schema.String),
});
export type PullRequestLabel = typeof PullRequestLabel.Type;

export const PullRequestListItem = Schema.Struct({
  provider: Schema.Literal("github"),
  host: TrimmedNonEmptyString,
  projectId: ProjectId,
  projectTitle: TrimmedNonEmptyString,
  repository: TrimmedNonEmptyString,
  number: PositiveInt,
  title: TrimmedNonEmptyString,
  url: TrimmedNonEmptyString,
  author: Schema.NullOr(PullRequestActor),
  baseRefName: TrimmedNonEmptyString,
  headRefName: TrimmedNonEmptyString,
  state: ChangeRequestState,
  isDraft: Schema.Boolean,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  viewerReviewRequested: Schema.Boolean,
  viewerAuthored: Schema.Boolean,
  labels: Schema.Array(PullRequestLabel),
  reviewDecision: Schema.optional(PullRequestReviewDecision),
  checksState: Schema.optional(PullRequestChecksState),
});
export type PullRequestListItem = typeof PullRequestListItem.Type;

export const PullRequestRepository = Schema.Struct({
  nameWithOwner: TrimmedNonEmptyString,
  url: TrimmedNonEmptyString,
});
export type PullRequestRepository = typeof PullRequestRepository.Type;

export const PullRequestExternalFallback = Schema.Union([
  Schema.TaggedStruct("Available", {
    url: TrimmedNonEmptyString,
  }),
  Schema.TaggedStruct("Unavailable", {
    reason: Schema.Literal("repository-url-unavailable"),
  }),
]);
export type PullRequestExternalFallback = typeof PullRequestExternalFallback.Type;

export const PullRequestListFailureReason = Schema.Literals([
  "missing-cli",
  "unauthenticated",
  "unsupported",
  "rate-limited",
  "project-failure",
  "operation-failure",
]);
export type PullRequestListFailureReason = typeof PullRequestListFailureReason.Type;

export const PullRequestListRecoveryAction = Schema.Literals([
  "install-github-cli",
  "authenticate-github-cli",
  "configure-github-remote",
  "retry-after-rate-limit",
  "retry",
]);
export type PullRequestListRecoveryAction = typeof PullRequestListRecoveryAction.Type;

export const PullRequestListFailure = Schema.Union([
  Schema.Struct({
    reason: Schema.Literal("missing-cli"),
    action: Schema.Literal("install-github-cli"),
    message: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    reason: Schema.Literal("unauthenticated"),
    action: Schema.Literal("authenticate-github-cli"),
    message: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    reason: Schema.Literal("unsupported"),
    action: Schema.Literal("configure-github-remote"),
    message: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    reason: Schema.Literal("rate-limited"),
    action: Schema.Literal("retry-after-rate-limit"),
    message: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    reason: Schema.Literal("project-failure"),
    action: Schema.Literal("retry"),
    message: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    reason: Schema.Literal("operation-failure"),
    action: Schema.Literal("retry"),
    message: TrimmedNonEmptyString,
  }),
]);
export type PullRequestListFailure = typeof PullRequestListFailure.Type;

const PullRequestListPartitionSharedFields = {
  projectId: ProjectId,
  projectTitle: TrimmedNonEmptyString,
  externalFallback: PullRequestExternalFallback,
} as const;

export const PullRequestListPartition = Schema.Union([
  Schema.Struct({
    ...PullRequestListPartitionSharedFields,
    status: Schema.Literal("ready"),
    repository: PullRequestRepository,
    entries: Schema.Array(PullRequestListItem),
  }),
  Schema.Struct({
    ...PullRequestListPartitionSharedFields,
    status: Schema.Literal("empty"),
    repository: PullRequestRepository,
  }),
  Schema.Struct({
    ...PullRequestListPartitionSharedFields,
    status: Schema.Literal("unavailable"),
    failure: PullRequestListFailure,
  }),
]);
export type PullRequestListPartition = typeof PullRequestListPartition.Type;

export const PullRequestListResult = Schema.Struct({
  partitions: Schema.Array(PullRequestListPartition),
});
export type PullRequestListResult = typeof PullRequestListResult.Type;

/**
 * Addresses one pull request for reads and writes. `projectId` picks the checkout the host CLI
 * runs in and, when its repository matches, the credentials; `host` lets the Environment route a
 * pull request in another repository through any AFAR Project on that host, which is what a
 * thread linking work outside its own project needs. Absent `host` means "this project's own
 * repository", which is every reference from before thread links became host-level.
 */
export const PullRequestRef = Schema.Struct({
  projectId: ProjectId,
  host: Schema.optional(TrimmedNonEmptyString),
  repository: TrimmedNonEmptyString,
  number: PositiveInt,
});
export type PullRequestRef = typeof PullRequestRef.Type;

export const PullRequestListStatsInput = Schema.Struct({
  refs: Schema.Array(PullRequestRef).check(Schema.isMaxLength(500)),
});
export type PullRequestListStatsInput = typeof PullRequestListStatsInput.Type;

export const PullRequestDiffStat = Schema.Struct({
  ...PullRequestRef.fields,
  additions: NonNegativeInt,
  deletions: NonNegativeInt,
});
export type PullRequestDiffStat = typeof PullRequestDiffStat.Type;

export const PullRequestListStatsResult = Schema.Struct({
  stats: Schema.Array(PullRequestDiffStat),
});
export type PullRequestListStatsResult = typeof PullRequestListStatsResult.Type;

export const PullRequestMergeability = Schema.Literals(["mergeable", "conflicting", "unknown"]);
export type PullRequestMergeability = typeof PullRequestMergeability.Type;

export const PullRequestMergeMethod = Schema.Literals(["merge", "squash", "rebase"]);
export type PullRequestMergeMethod = typeof PullRequestMergeMethod.Type;

export const PullRequestAction = Schema.Literals([
  "merge",
  "ready",
  "draft",
  "close",
  "reopen",
  "update-branch",
  "enable-auto-merge",
  "disable-auto-merge",
]);
export type PullRequestAction = typeof PullRequestAction.Type;

export const PullRequestUpdateMethod = Schema.Literals(["merge", "rebase"]);
export type PullRequestUpdateMethod = typeof PullRequestUpdateMethod.Type;

export const PullRequestReviewVerdict = Schema.Literals(["comment", "approve", "request-changes"]);
export type PullRequestReviewVerdict = typeof PullRequestReviewVerdict.Type;

export const PullRequestReviewCapabilities = Schema.Struct({
  inlineComment: Schema.Boolean,
  reply: Schema.Boolean,
  resolve: Schema.Boolean,
  verdicts: Schema.Array(PullRequestReviewVerdict),
});
export type PullRequestReviewCapabilities = typeof PullRequestReviewCapabilities.Type;

export const PullRequestEditCapabilities = Schema.Struct({
  changeRequest: Schema.Boolean,
  comment: Schema.Boolean,
});
export type PullRequestEditCapabilities = typeof PullRequestEditCapabilities.Type;

export const PullRequestReviewerCapabilities = Schema.Struct({
  request: Schema.Boolean,
  listCandidates: Schema.Boolean,
});
export type PullRequestReviewerCapabilities = typeof PullRequestReviewerCapabilities.Type;

export const PullRequestCapabilities = Schema.Struct({
  diff: Schema.Boolean,
  comment: Schema.Boolean,
  actions: Schema.Array(PullRequestAction),
  mergeMethods: Schema.Array(PullRequestMergeMethod),
  updateMethods: Schema.optional(Schema.Array(PullRequestUpdateMethod)),
  search: Schema.Boolean,
  reactions: Schema.optional(Schema.Boolean),
  /** Whether this provider can be told the whole set of labels a pull request should carry. */
  labels: Schema.optional(Schema.Boolean),
  review: PullRequestReviewCapabilities,
  reviewers: PullRequestReviewerCapabilities,
  edit: Schema.optional(PullRequestEditCapabilities),
});
export type PullRequestCapabilities = typeof PullRequestCapabilities.Type;

export const PullRequestViewerPermissions = Schema.Struct({
  editChangeRequest: Schema.optionalKey(Schema.Boolean),
  actions: Schema.Array(PullRequestAction),
  comment: Schema.Boolean,
  resolve: Schema.Boolean,
  verdicts: Schema.Array(PullRequestReviewVerdict),
  requestReviewers: Schema.Boolean,
  /**
   * Whether this viewer may change the pull request's labels. Not the same as being able to edit
   * it: a author can retitle their own pull request without being able to label it.
   */
  labels: Schema.optional(Schema.Boolean),
  updateMethods: Schema.optional(Schema.Array(PullRequestUpdateMethod)),
});
export type PullRequestViewerPermissions = typeof PullRequestViewerPermissions.Type;

export const PullRequestCheckStatus = Schema.Literals([
  "pending",
  "success",
  "failure",
  "skipped",
  "neutral",
  "cancelled",
]);
export type PullRequestCheckStatus = typeof PullRequestCheckStatus.Type;

export const PullRequestCheck = Schema.Struct({
  name: TrimmedNonEmptyString,
  status: PullRequestCheckStatus,
  description: Schema.NullOr(Schema.String),
  url: Schema.NullOr(Schema.String),
});
export type PullRequestCheck = typeof PullRequestCheck.Type;

export const PullRequestMergeCapabilities = Schema.Struct({
  merge: Schema.Boolean,
  squash: Schema.Boolean,
  rebase: Schema.Boolean,
});
export type PullRequestMergeCapabilities = typeof PullRequestMergeCapabilities.Type;

export const PullRequestDetailInput = Schema.Struct({
  ...PullRequestRef.fields,
  authorityEpoch: TrimmedNonEmptyString.check(Schema.isMaxLength(200)),
});
export type PullRequestDetailInput = typeof PullRequestDetailInput.Type;

export const PullRequestDetail = Schema.Struct({
  autoMergeAllowed: Schema.optionalKey(Schema.Boolean),
  autoMergeMethod: Schema.optionalKey(Schema.NullOr(PullRequestMergeMethod)),
  headRefOid: Schema.optionalKey(TrimmedNonEmptyString),
  baseRefOid: Schema.optionalKey(TrimmedNonEmptyString),
  projectRevision: Schema.optionalKey(TrimmedNonEmptyString),
  provider: Schema.Literal("github"),
  capabilities: PullRequestCapabilities,
  viewerPermissions: PullRequestViewerPermissions,
  projectId: ProjectId,
  projectTitle: TrimmedNonEmptyString,
  repository: TrimmedNonEmptyString,
  number: PositiveInt,
  title: TrimmedNonEmptyString,
  body: Schema.String,
  url: TrimmedNonEmptyString,
  author: Schema.NullOr(PullRequestActor),
  state: ChangeRequestState,
  isDraft: Schema.Boolean,
  mergeability: PullRequestMergeability,
  additions: NonNegativeInt,
  deletions: NonNegativeInt,
  changedFiles: NonNegativeInt,
  headRefName: TrimmedNonEmptyString,
  baseRefName: TrimmedNonEmptyString,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  mergedAt: Schema.NullOr(IsoDateTime),
  closedAt: Schema.NullOr(IsoDateTime),
  reviewers: Schema.Array(PullRequestActor),
  labels: Schema.Array(PullRequestLabel),
  checks: Schema.Array(PullRequestCheck),
  mergeCapabilities: PullRequestMergeCapabilities,
  viewer: Schema.optional(TrimmedNonEmptyString),
  resourceVersion: TrimmedNonEmptyString,
});
export type PullRequestDetail = typeof PullRequestDetail.Type;

export const PullRequestDetailResult = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("ready"),
    detail: PullRequestDetail,
    externalFallback: PullRequestExternalFallback,
  }),
  Schema.Struct({
    status: Schema.Literal("unavailable"),
    ...PullRequestRef.fields,
    failure: PullRequestListFailure,
    externalFallback: PullRequestExternalFallback,
  }),
]);
export type PullRequestDetailResult = typeof PullRequestDetailResult.Type;

export const PullRequestTimelineInput = PullRequestDetailInput;
export type PullRequestTimelineInput = typeof PullRequestTimelineInput.Type;

export const PullRequestActivityKind = Schema.Literals([
  "closed",
  "reopened",
  "merged",
  "ready-for-review",
  "converted-to-draft",
  "force-pushed",
  "review-requested",
  "review-request-removed",
  "labeled",
  "unlabeled",
  "title-changed",
  "review-submitted",
]);
export type PullRequestActivityKind = typeof PullRequestActivityKind.Type;

export const PullRequestActivity = Schema.Struct({
  id: TrimmedNonEmptyString,
  kind: PullRequestActivityKind,
  occurredAt: IsoDateTime,
  actor: Schema.NullOr(PullRequestActor),
  summary: TrimmedNonEmptyString,
});
export type PullRequestActivity = typeof PullRequestActivity.Type;

export const PullRequestTimelineCommit = Schema.Struct({
  oid: TrimmedNonEmptyString,
  url: TrimmedNonEmptyString,
  messageHeadline: TrimmedNonEmptyString,
  messageBody: Schema.String,
  committedAt: IsoDateTime,
  author: Schema.NullOr(PullRequestActor),
});
export type PullRequestTimelineCommit = typeof PullRequestTimelineCommit.Type;

/**
 * The reactions a host reports on one comment or on the pull request itself. The viewer's own
 * reaction is carried with the count, because a reader needs to know which of them is theirs
 * before they can take it back.
 */
export const PullRequestReactionContent = Schema.Literals([
  "THUMBS_UP",
  "THUMBS_DOWN",
  "LAUGH",
  "HOORAY",
  "CONFUSED",
  "HEART",
  "ROCKET",
  "EYES",
]);
export type PullRequestReactionContent = typeof PullRequestReactionContent.Type;

export const PullRequestReaction = Schema.Struct({
  content: PullRequestReactionContent,
  count: NonNegativeInt,
  viewerHasReacted: Schema.Boolean,
});
export type PullRequestReaction = typeof PullRequestReaction.Type;

export const PullRequestTimelineComment = Schema.Struct({
  id: TrimmedNonEmptyString,
  url: TrimmedNonEmptyString,
  body: Schema.String,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  author: Schema.NullOr(PullRequestActor),
  viewerDidAuthor: Schema.Boolean,
  /** Absent on an Environment that has not been taught to read them; never assumed to be none. */
  reactions: Schema.optionalKey(Schema.Array(PullRequestReaction).check(Schema.isMaxLength(8))),
});
export type PullRequestTimelineComment = typeof PullRequestTimelineComment.Type;

/**
 * One pull request's own checks, read when a reader asks to see them rather than with the
 * listing. A page of rows carries the rollup each one reports; the runs behind it are a hundred
 * times that, and most are never looked at.
 *
 * Evidence of what the host last said, like every other read here: it never establishes
 * permission and never makes a hosted action eligible.
 */
export const PullRequestChecksInput = PullRequestRef;
export type PullRequestChecksInput = typeof PullRequestChecksInput.Type;

export const PullRequestChecksResult = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("ready"),
    ...PullRequestRef.fields,
    checks: Schema.Array(PullRequestCheck).check(Schema.isMaxLength(200)),
  }),
  Schema.Struct({
    status: Schema.Literal("unavailable"),
    ...PullRequestRef.fields,
    failure: PullRequestListFailure,
  }),
]);
export type PullRequestChecksResult = typeof PullRequestChecksResult.Type;

export const PullRequestReviewThread = Schema.Struct({
  id: TrimmedNonEmptyString,
  isResolved: Schema.Boolean,
  isOutdated: Schema.Boolean,
  path: TrimmedNonEmptyString,
  line: Schema.NullOr(PositiveInt),
  startLine: Schema.NullOr(PositiveInt),
  comments: Schema.Array(PullRequestTimelineComment).check(Schema.isMaxLength(100)),
});
export type PullRequestReviewThread = typeof PullRequestReviewThread.Type;

export const PullRequestTimelineResult = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("ready"),
    ...PullRequestRef.fields,
    activity: Schema.Array(PullRequestActivity).check(Schema.isMaxLength(100)),
    commits: Schema.Array(PullRequestTimelineCommit).check(Schema.isMaxLength(100)),
    comments: Schema.Array(PullRequestTimelineComment).check(Schema.isMaxLength(100)),
    reviewThreads: Schema.Array(PullRequestReviewThread).check(Schema.isMaxLength(100)),
    resourceVersion: TrimmedNonEmptyString,
    /**
     * The AFAR Project revision this activity was read at. Carried so a remark can be reacted to
     * from here without a second, differently-scoped read of the pull request's detail.
     */
    projectRevision: Schema.optionalKey(TrimmedNonEmptyString),
    externalFallback: PullRequestExternalFallback,
  }),
  Schema.Struct({
    status: Schema.Literal("unavailable"),
    ...PullRequestRef.fields,
    failure: PullRequestListFailure,
    externalFallback: PullRequestExternalFallback,
  }),
]);
export type PullRequestTimelineResult = typeof PullRequestTimelineResult.Type;

export const SourceControlRepositoryCloneUrls = Schema.Struct({
  nameWithOwner: TrimmedNonEmptyString,
  url: TrimmedNonEmptyString,
  sshUrl: TrimmedNonEmptyString,
});
export type SourceControlRepositoryCloneUrls = typeof SourceControlRepositoryCloneUrls.Type;

export const SourceControlRepositoryVisibility = Schema.Literals(["private", "public"]);
export type SourceControlRepositoryVisibility = typeof SourceControlRepositoryVisibility.Type;

export const SourceControlCloneProtocol = Schema.Literals(["auto", "ssh", "https"]);
export type SourceControlCloneProtocol = typeof SourceControlCloneProtocol.Type;

export const SourceControlRepositoryInfo = Schema.Struct({
  provider: SourceControlProviderKind,
  nameWithOwner: TrimmedNonEmptyString,
  url: TrimmedNonEmptyString,
  sshUrl: TrimmedNonEmptyString,
});
export type SourceControlRepositoryInfo = typeof SourceControlRepositoryInfo.Type;

export const SourceControlRepositoryLookupInput = Schema.Struct({
  provider: SourceControlProviderKind,
  repository: TrimmedNonEmptyString,
  cwd: Schema.optional(TrimmedNonEmptyString),
});
export type SourceControlRepositoryLookupInput = typeof SourceControlRepositoryLookupInput.Type;

export const SourceControlCloneRepositoryInput = Schema.Struct({
  provider: Schema.optional(SourceControlProviderKind),
  repository: Schema.optional(TrimmedNonEmptyString),
  remoteUrl: Schema.optional(TrimmedNonEmptyString),
  destinationPath: TrimmedNonEmptyString,
  protocol: Schema.optional(SourceControlCloneProtocol),
});
export type SourceControlCloneRepositoryInput = typeof SourceControlCloneRepositoryInput.Type;

export const SourceControlCloneRepositoryResult = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  remoteUrl: TrimmedNonEmptyString,
  repository: Schema.NullOr(SourceControlRepositoryInfo),
});
export type SourceControlCloneRepositoryResult = typeof SourceControlCloneRepositoryResult.Type;

export const SourceControlPublishRepositoryInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  provider: SourceControlProviderKind,
  repository: TrimmedNonEmptyString,
  visibility: SourceControlRepositoryVisibility,
  remoteName: Schema.optional(TrimmedNonEmptyString),
  protocol: Schema.optional(SourceControlCloneProtocol),
});
export type SourceControlPublishRepositoryInput = typeof SourceControlPublishRepositoryInput.Type;

export const SourceControlPublishStatus = Schema.Literals(["pushed", "remote_added"]);
export type SourceControlPublishStatus = typeof SourceControlPublishStatus.Type;

export const SourceControlPublishRepositoryResult = Schema.Struct({
  repository: SourceControlRepositoryInfo,
  remoteName: TrimmedNonEmptyString,
  remoteUrl: TrimmedNonEmptyString,
  branch: TrimmedNonEmptyString,
  upstreamBranch: Schema.optional(TrimmedNonEmptyString),
  status: SourceControlPublishStatus,
});
export type SourceControlPublishRepositoryResult = typeof SourceControlPublishRepositoryResult.Type;

export const SourceControlDiscoveryStatus = Schema.Literals(["available", "missing"]);
export type SourceControlDiscoveryStatus = typeof SourceControlDiscoveryStatus.Type;

export const SourceControlProviderAuthStatus = Schema.Literals([
  "authenticated",
  "unauthenticated",
  "unknown",
]);
export type SourceControlProviderAuthStatus = typeof SourceControlProviderAuthStatus.Type;

export const SourceControlProviderAuth = Schema.Struct({
  status: SourceControlProviderAuthStatus,
  account: Schema.Option(TrimmedNonEmptyString),
  host: Schema.Option(TrimmedNonEmptyString),
  detail: Schema.Option(TrimmedNonEmptyString),
});
export type SourceControlProviderAuth = typeof SourceControlProviderAuth.Type;

const SourceControlDiscoverySharedFields = {
  label: TrimmedNonEmptyString,
  executable: Schema.optional(TrimmedNonEmptyString),
  status: SourceControlDiscoveryStatus,
  version: Schema.Option(TrimmedNonEmptyString),
  installHint: TrimmedNonEmptyString,
  detail: Schema.Option(TrimmedNonEmptyString),
} as const;

export const VcsDiscoveryItem = Schema.Struct({
  kind: VcsDriverKind,
  implemented: Schema.Boolean,
  ...SourceControlDiscoverySharedFields,
});
export type VcsDiscoveryItem = typeof VcsDiscoveryItem.Type;

export const SourceControlProviderDiscoveryItem = Schema.Struct({
  kind: SourceControlProviderKind,
  ...SourceControlDiscoverySharedFields,
  auth: SourceControlProviderAuth,
});
export type SourceControlProviderDiscoveryItem = typeof SourceControlProviderDiscoveryItem.Type;

export const SourceControlDiscoveryResult = Schema.Struct({
  versionControlSystems: Schema.Array(VcsDiscoveryItem),
  sourceControlProviders: Schema.Array(SourceControlProviderDiscoveryItem),
});
export type SourceControlDiscoveryResult = typeof SourceControlDiscoveryResult.Type;

export const GitHubAuthenticationStatus = Schema.Struct({
  state: Schema.Literals(["disconnected", "waiting", "connected", "failed"]),
  account: Schema.Option(TrimmedNonEmptyString),
  detail: Schema.Option(TrimmedNonEmptyString),
  userCode: Schema.Option(TrimmedNonEmptyString),
  attemptId: Schema.Option(CommandId),
});
export type GitHubAuthenticationStatus = typeof GitHubAuthenticationStatus.Type;

export const GitHubAuthenticationStartResult = Schema.Struct({
  attemptId: CommandId,
  verificationUrl: TrimmedNonEmptyString,
  userCode: TrimmedNonEmptyString,
});
export type GitHubAuthenticationStartResult = typeof GitHubAuthenticationStartResult.Type;

export class GitHubAuthenticationError extends Schema.TaggedErrorClass<GitHubAuthenticationError>()(
  "GitHubAuthenticationError",
  {
    reason: Schema.Literals(["busy", "unavailable", "failed", "forbidden"]),
    detail: Schema.String,
  },
) {}

export class SourceControlProviderError extends Schema.TaggedErrorClass<SourceControlProviderError>()(
  "SourceControlProviderError",
  {
    provider: SourceControlProviderKind,
    operation: Schema.String,
    cwd: Schema.String,
    command: Schema.optional(Schema.String),
    repository: Schema.optional(Schema.String),
    reference: Schema.optional(Schema.String),
    detail: Schema.String,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  override get message(): string {
    return `Source control provider ${this.provider} failed in ${this.operation}: ${this.detail}`;
  }
}

export class SourceControlRepositoryError extends Schema.TaggedErrorClass<SourceControlRepositoryError>()(
  "SourceControlRepositoryError",
  {
    provider: SourceControlProviderKind,
    operation: Schema.String,
    detail: Schema.String,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  override get message(): string {
    return `Source control repository operation ${this.operation} failed for ${this.provider}: ${this.detail}`;
  }
}

export const PullRequestCodeInput = Schema.Struct({
  ...PullRequestDetailInput.fields,
  page: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 100 })),
  resourceVersion: Schema.optionalKey(TrimmedNonEmptyString),
  expandedPath: Schema.optionalKey(TrimmedNonEmptyString),
});
export type PullRequestCodeInput = typeof PullRequestCodeInput.Type;
export const PullRequestCodeFile = Schema.Struct({
  path: TrimmedNonEmptyString,
  previousPath: Schema.NullOr(Schema.String),
  status: Schema.String,
  additions: Schema.Int,
  deletions: Schema.Int,
  patch: Schema.NullOr(Schema.String.check(Schema.isMaxLength(1_000_000))),
});
export type PullRequestCodeFile = typeof PullRequestCodeFile.Type;
export const PullRequestCodeResult = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("ready"),
    ...PullRequestRef.fields,
    files: Schema.Array(PullRequestCodeFile).check(Schema.isMaxLength(30)),
    page: Schema.Int,
    nextPage: Schema.NullOr(Schema.Int),
    resourceVersion: TrimmedNonEmptyString,
    externalFallback: PullRequestExternalFallback,
  }),
  Schema.Struct({
    status: Schema.Literal("unavailable"),
    ...PullRequestRef.fields,
    failure: PullRequestListFailure,
    externalFallback: PullRequestExternalFallback,
  }),
]);
export type PullRequestCodeResult = typeof PullRequestCodeResult.Type;
